import { useEffect, useState } from 'react';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';

/**
 * What a COUNTRY allows this doctor, and the defaults it implies — the doctor
 * BFF's `myCapabilities` (PM-0218, docs/country-config/CONTRACT.md §4 in the
 * monorepo): its e-prescription provider, council register, messaging channels,
 * payment rails and bill services, and its language, calendar, week, currency,
 * zone, national-id rule, dial code, address model and map.
 *
 *   useDoctorCapabilities()          the doctor's own country — the one they
 *                                    stated, else the deployment's (IR on
 *                                    Parsina): for the doctor's OWN screens;
 *   useDoctorCapabilities(clinicId)  one of the doctor's clinics (the BFF refuses
 *                                    any other, which reads as null here).
 *
 * Never the clinical host's: the visit room and the patient record run on the
 * VISIT's clinic, and a doctor's own country is not it — an Iranian clinic's
 * visit by a doctor who stated Iraq keeps Iran's rails and defaults. App.jsx
 * hands <ClinicalHostProvider> no capabilities until they are per visit.
 *
 * Returns the object, or `null` while it is unknown: before the first answer and
 * on any error. `null` means "behave as before" to every caller, which is why
 * every failure lands on it rather than on a guess.
 *
 * Asked in its OWN document, never added to another query: a panel newer than
 * its BFF must lose only this answer (the field does not exist there yet), never
 * the screen it sits on.
 *
 * One answer per clinic (and one for the doctor's own), kept for five minutes
 * and shared by every caller; a failed read is forgotten, so the next mount asks
 * again.
 */

export const DOCTOR_CAPABILITIES_QUERY = `
  query DoctorCapabilities($clinicId: ID) {
    myCapabilities(clinicId: $clinicId) {
      country erx councilRegistry smsProvider messagingChannels paymentRails billServices
      payrollProfile insuranceCatalog defaultLanguage calendar weekStart weekendDays
      defaultCurrency defaultTimezone nationalIdRule nationalDialCode addressModel
      mapView { lat lon zoom }
      printCalendar printLanguage
    }
  }
`;

const TTL_MS = 5 * 60 * 1000;

// '' (the doctor's own) or a clinic id → { promise, value, at } — `value` once answered.
const answers = new Map();

function load(key) {
  const hit = answers.get(key);
  if (hit && (!('value' in hit) || Date.now() - hit.at < TTL_MS)) return hit.promise;
  const entry = {};
  entry.promise = bffQuery(DOCTOR_CAPABILITIES_QUERY, key ? { clinicId: key } : {})
    .then((data) => {
      const caps = data?.myCapabilities;
      const value = caps && typeof caps === 'object' ? caps : null;
      entry.value = value;
      entry.at = Date.now();
      if (!value) answers.delete(key);
      return value;
    })
    .catch(() => {
      answers.delete(key);
      return null;
    });
  answers.set(key, entry);
  return entry.promise;
}

function known(key) {
  const hit = answers.get(key);
  return hit && 'value' in hit ? hit.value : null;
}

/** Tests only: forget every answer. */
export function resetDoctorCapabilities() {
  answers.clear();
}

/** The `Capabilities` of the doctor's own country, or of one of their clinics; null while unknown. */
export function useDoctorCapabilities(clinicId = null) {
  const key = typeof clinicId === 'string' ? clinicId.trim() : '';
  const [state, setState] = useState(() => ({ key, value: known(key) }));

  useEffect(() => {
    let live = true;
    load(key).then((value) => {
      if (live) setState((prev) => (prev.key === key && prev.value === value ? prev : { key, value }));
    });
    return () => { live = false; };
  }, [key]);

  return state.key === key ? state.value : known(key);
}

export default useDoctorCapabilities;
